/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { Setting } from "../settings/settings";

/**
 * Extended settings for Nevai Meet.
 *
 * Everything here is 100% local / client-side and FREE to use. No external
 * API, no paid service, no AI. All preferences are persisted to localStorage
 * via the `Setting` primitive defined in `./settings`.
 */

/* ------------------------------------------------------------------ */
/* Privacy & Security                                                  */
/* ------------------------------------------------------------------ */

/** Share last-seen / online status with others */
export const shareOnlineStatus = new Setting<boolean>(
  "privacy-share-online-status",
  true,
);

/** Locally-stored TOTP secret (base32). Empty = 2FA not configured. */
export const totpSecret = new Setting<string>("security-totp-secret", "");

/* ------------------------------------------------------------------ */
/* UI / UX & Customization                                            */
/* ------------------------------------------------------------------ */

/** Default call type created from the home screen: "meet", "coffee" (audio only) or "video" (HD). */
export const defaultCallType = new Setting<"meet" | "coffee" | "video">(
  "default-call-type",
  "meet",
);

export interface CustomTheme {
  name: string;
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

/** User-defined custom themes */
export const customThemes = new Setting<CustomTheme[]>("theme-custom-list", []);

/** Currently active custom theme id/name ("" = default Compound theme) */
export const activeTheme = new Setting<string>("theme-active", "");

/** Call layout: "grid" | "spotlight" | "minimalist" */
export const callLayout = new Setting<"grid" | "spotlight" | "minimalist">(
  "layout-call",
  "grid",
);

/** Global UI scale multiplier (0.8 - 1.4) */
export const uiScale = new Setting<number>("ui-scale", 1);

/** Font family applied app-wide */
export const uiFontFamily = new Setting<string>(
  "ui-font-family",
  "'Inter', system-ui, sans-serif",
);

/** High contrast mode */
export const highContrast = new Setting<boolean>("ui-high-contrast", false);

/* ------------------------------------------------------------------ */
/* Keyboard shortcuts                                                  */
/* ------------------------------------------------------------------ */

export interface ShortcutMap {
  toggleMute: string;
  toggleCamera: string;
  toggleScreenShare: string;
  toggleRecording: string;
  raiseHand: string;
  pushToTalk: string;
  toggleWhiteboard: string;
  togglePiP: string;
}

export const defaultShortcuts: ShortcutMap = {
  toggleMute: "m",
  toggleCamera: "v",
  toggleScreenShare: "s",
  toggleRecording: "r",
  raiseHand: "h",
  pushToTalk: "t",
  toggleWhiteboard: "w",
  togglePiP: "p",
};

export const shortcuts = new Setting<ShortcutMap>(
  "shortcuts-map",
  defaultShortcuts,
);

/* ------------------------------------------------------------------ */
/* Data & Storage                                                      */
/* ------------------------------------------------------------------ */

/** Whether a local backup of the entire app state has been taken */
export const lastBackupAt = new Setting<number | null>("data-last-backup", null);

/** Cleared media cache size tracking (bytes) */
export const cachedMediaBytes = new Setting<number>("data-cached-media-bytes", 0);

/* ------------------------------------------------------------------ */
/* Meeting / Media feature defaults                                    */
/* ------------------------------------------------------------------ */

/** Start calls with microphone muted */
export const joinMuted = new Setting<boolean>("join-muted", false);

/** Start calls with camera off */
export const joinCameraOff = new Setting<boolean>("join-camera-off", false);

/** Data saver: force 360p @ 15fps */
export const dataSaver = new Setting<boolean>("media-data-saver", false);

/** Default camera resolution when not in data-saver mode */
export const cameraResolution = new Setting<
  "360" | "720" | "1080" | "1440" | "2160" | "4320" | "11264"
>("media-camera-resolution", "720");

/** Default camera frame rate */
export const cameraFrameRate = new Setting<15 | 30 | 60>(
  "media-camera-fps",
  30,
);

/** Enable echo cancellation on the mic */
export const echoCancellation = new Setting<boolean>(
  "media-echo-cancellation",
  true,
);

/** Enable noise suppression on the mic */
export const noiseSuppression = new Setting<boolean>(
  "media-noise-suppression",
  true,
);

/** Enable the local 5-band equalizer */
export const equalizerEnabled = new Setting<boolean>("audio-eq-enabled", false);

/** 5 EQ band gains in dB (-12..12) */
export const equalizerBands = new Setting<number[]>(
  "audio-eq-bands",
  [0, 0, 0, 0, 0],
);

/** Voice changer preset: "none" | "deep" | "chipmunk" | "robot" */
export const voiceChangerPreset = new Setting<
  "none" | "deep" | "chipmunk" | "robot"
>("audio-voice-changer", "none");

/** Local noise-gate threshold in dBFS (-100..0) */
export const noiseGateThreshold = new Setting<number>(
  "audio-noise-gate",
  -50,
);

/** Push-to-talk enabled (mute by default, unmute while hotkey held) */
export const pushToTalk = new Setting<boolean>("audio-ptt", false);

/** Video filter preset */
export const videoFilter = new Setting<
  "none" | "brightness" | "contrast" | "saturate" | "sepia" | "grayscale" | "invert"
>("video-filter", "none");

/** Virtual background image data URL ("" = disabled) */
export const virtualBackground = new Setting<string>("video-virtual-bg", "");

/* ------------------------------------------------------------------ */
/* Accessibility                                                       */
/* ------------------------------------------------------------------ */

/** Reduce non-essential animations across the UI */
export const reduceMotion = new Setting<boolean>("ui-reduce-motion", false);

/** Enlarge interactive hit areas for touch / motor accessibility */
export const largerTapTargets = new Setting<boolean>("ui-larger-tap", false);

/** Always show a visible focus ring on keyboard navigation */
export const focusOutline = new Setting<boolean>("ui-focus-outline", false);

/* ------------------------------------------------------------------ */
/* Live captions (Web Speech API, fully local)                         */
/* ------------------------------------------------------------------ */

export const liveCaptions = new Setting<boolean>("captions-live", false);

/* ------------------------------------------------------------------ */
/* Local call history (stored in the browser only)                     */
/* ------------------------------------------------------------------ */

export interface CallHistoryEntry {
  id: string;
  roomName: string;
  startedAt: number;
  durationMs: number;
  participants: number;
}

export const callHistory = new Setting<CallHistoryEntry[]>("call-history", []);

export const maxCallHistory = new Setting<number>("call-history-max", 50);

/* ------------------------------------------------------------------ */
/* Presence / status broadcast to the Matrix homeserver                 */
/* ------------------------------------------------------------------ */

export type PresenceStatus = "online" | "unavailable" | "offline";

export const userStatus = new Setting<PresenceStatus>("presence-status", "online");

export const userStatusMessage = new Setting<string>("presence-status-msg", "");

/* ------------------------------------------------------------------ */
/* Call quality (no-limits audio / video)                               */
/* ------------------------------------------------------------------ */

export type CallQuality = "auto" | "high" | "max";

export const callQuality = new Setting<CallQuality>("call-quality", "auto");

/** Preferred camera bitrate in kbps. 0 = let LiveKit decide (adaptive). */
export const videoBitrateKbps = new Setting<number>(
  "video-bitrate-kbps",
  0,
);

/* ------------------------------------------------------------------ */
/* Call timer overlay                                                   */
/* ------------------------------------------------------------------ */

export const showCallTimer = new Setting<boolean>("show-call-timer", true);
